// The status badge on an order (KOVA-797) — a port of the mobile app's `features/orders/model/status.ts`.
// The wire pair (`status` + `substatus`) picks one display status; on the list the section and a bound
// renewal action can change it, and a past order dates it. An order whose status this app doesn't know
// (`status: null`) gets no badge rather than a guessed one.
import type { BannerAction, OrderStatus, OrderSubstatus, OrderSummary } from '@/api/medOrders'
import { formatCompletedDate } from '@/orders/format'

export type DisplayStatus =
	| 'submitted'
	| 'reviewing'
	| 'approved'
	| 'dispensed'
	| 'onTheWay'
	| 'delivered'
	| 'inTreatment'
	| 'paymentNeeded'
	| 'actionNeeded'
	| 'canceled'
	| 'deliveryFailed'
	| 'returned'

/** The pill colours. `grey` is never a status's own: it marks a dated past badge and the details' "Delivered". */
export type StatusTone = 'green' | 'blue' | 'orange' | 'red' | 'grey'

export interface StatusBadge {
	label: string
	tone: StatusTone
}

export const STATUS_LABEL: Record<DisplayStatus, string> = {
	submitted: 'Submitted',
	reviewing: 'Reviewing',
	approved: 'Approved',
	dispensed: 'Dispensed',
	onTheWay: 'On the way',
	delivered: 'Delivered',
	inTreatment: 'In treatment',
	paymentNeeded: 'Payment needed',
	actionNeeded: 'Action needed',
	canceled: 'Canceled',
	deliveryFailed: 'Delivery failed',
	returned: 'Returned',
}

export const STATUS_TONE: Record<DisplayStatus, StatusTone> = {
	submitted: 'green',
	reviewing: 'green',
	approved: 'green',
	dispensed: 'blue',
	onTheWay: 'blue',
	delivered: 'green',
	inTreatment: 'green',
	paymentNeeded: 'orange',
	actionNeeded: 'orange',
	canceled: 'red',
	deliveryFailed: 'red',
	returned: 'orange',
}

// `health_check_expired` stays contract-only: no design draws it, so it never changes a badge.
const ACTION_STATUS: Record<BannerAction, DisplayStatus | null> = {
	payment_needed: 'paymentNeeded',
	health_check_due: 'actionNeeded',
	health_check_expired: null,
}

export function actionDisplayStatus(action: BannerAction): DisplayStatus | null {
	return ACTION_STATUS[action]
}

/**
 * The display status of a wire pair. `inProgress`: the order sits in the list's "In progress" — a delivered
 * order there is "In treatment". Without it (the past list, the details) delivered is "Delivered".
 */
export function displayOrderStatus(
	status: OrderStatus | null,
	substatus: OrderSubstatus | null,
	options: { inProgress?: boolean } = {},
): DisplayStatus | null {
	switch (status) {
		case 'submitted':
			return substatus === 'reviewing' ? 'reviewing' : 'submitted'
		case 'approved':
			return substatus === 'dispensed' ? 'dispensed' : 'approved'
		case 'shipped':
			return 'onTheWay'
		case 'delivered':
			return options.inProgress ? 'inTreatment' : 'delivered'
		case 'canceled':
			return 'canceled'
		case 'delivery_failed':
			return 'deliveryFailed'
		case 'returned':
			return 'returned'
		default:
			return null
	}
}

/**
 * A list card's badge. In progress, the order's bound renewal action (`banner.orderId === order.id`) wins
 * over its status, and the badge is coloured and dateless — a terminal order still in progress too. A past
 * order with a readable `completedAt` reads grey "Delivered · Jun 5"; without one it keeps its colour.
 */
export function listBadge(
	order: Pick<OrderSummary, 'status' | 'substatus' | 'completedAt'>,
	options: { inProgress: boolean; action?: BannerAction | null },
): StatusBadge | null {
	const override = options.inProgress && options.action ? actionDisplayStatus(options.action) : null
	const status = override ?? displayOrderStatus(order.status, order.substatus, { inProgress: options.inProgress })
	if (status === null) return null
	const date = !options.inProgress && order.completedAt !== null ? formatCompletedDate(order.completedAt) : null
	if (date !== null) return { label: `${STATUS_LABEL[status]} · ${date}`, tone: 'grey' }
	return { label: STATUS_LABEL[status], tone: STATUS_TONE[status] }
}

/**
 * The details badge: the status alone — no section ("Delivered", never "In treatment"), no action, no date.
 * "Delivered" is grey as Figma draws it (the mobile app draws it green; docs/architecture/orders.md, Design deviations).
 */
export function detailBadge(order: Pick<OrderSummary, 'status' | 'substatus'>): StatusBadge | null {
	const status = displayOrderStatus(order.status, order.substatus)
	if (status === null) return null
	return { label: STATUS_LABEL[status], tone: status === 'delivered' ? 'grey' : STATUS_TONE[status] }
}
